import * as React from 'react';
import { connect } from 'react-redux';
import { Button, Container, Form, FormFeedback, Label } from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';
import Select from 'react-select';

import * as AidsApi from '../../api/aids';
import * as UtilApi from '../../api/util';

import { ADMIN, CITY_HALL } from '../../constants/accountType';

import Localization from '../../config/localization';

import BackButton from '../../components/BackButton';
import Loader from '../../components/Loader';
import ReportType from '../../components/ReportType';
import SearchInput from '../../components/SearchInput';
import ElectricEnergySvg from '../../components/Svg/electricEnergy';
import NaturalGasSvg from '../../components/Svg/naturalGas';
import StopSvg from '../../components/Svg/stop';

import Notifications from '../../lib/notifications';
import { DownloadFile, ToNumber, ToString } from '../../lib/utils';

import './Report.scss';
import DatePicker from '../../components/DatePicker';
import { MIN_ADDITION_DATE } from '../../constants/addition';
import ReportJob from './ReportJob';
import { isAccountType } from '../../lib/user';
import { getCurrentDate } from '../../lib/date';
import moment from 'moment';

type IProps = Report.IProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const DATE_FORMAT = 'YYYYMM';

enum SEASONS {
  PREVIOUS = 0,
  CURRENT = 1
}

class Report extends React.Component<IProps, Report.IState> {
  state: Report.IState = {
    loading: false,
    loadingMessage: '',

    cityHalls: undefined,
    selectedCityHall: undefined,

    eeCount: 0,
    gnCount: 0,
    eeCountAddition: 0,
    gnCountAddition: 0,
    reportType: Localization.reportTypeOptions[0].value as Report.ReportType,
    season: SEASONS.CURRENT,
    startDate: getCurrentDate(DATE_FORMAT)
  };

  async componentDidMount() {
    return this._countAids();
  }

  componentDidUpdate(_prevProps: Readonly<IProps>, prevState: Readonly<Report.IState>): void {
    if (prevState.startDate !== this.state.startDate) {
      if (moment(this.state.startDate).isSameOrAfter(moment(getCurrentDate(DATE_FORMAT)))) {
        this.setState({ season: SEASONS.CURRENT });
      } else {
        this.setState({ season: SEASONS.PREVIOUS });
      }
    }
  }

  renderIcon = (reportType: Report.ReportType) => {
    if (['anexa1b', 'anexa15', 'anexa16'].indexOf(reportType) > -1) {
      return <NaturalGasSvg className="mx-1" />;
    } else if (reportType === 'anexa1c') {
      return <ElectricEnergySvg className="mx-1" />;
    }

    return (
      <React.Fragment>
        <NaturalGasSvg className="m-0 mb-2 two-svg" />
        <ElectricEnergySvg className="m-0 mt-2 two-svg" />
      </React.Fragment>
    );
  };

  _excludedReports = (reportType = this.state.reportType) => {
    return ['exportBeneficiary', 'exportSupliment'].indexOf(reportType) > -1;
  };

  renderReportsContainer = () => {
    return (
      <div className="my-5 d-flex flex-wrap">
        {Localization.reportTypeOptions.map(report => {
          const reportType = report.value as Report.ReportType;

          if (this._excludedReports(reportType)) {
            if ((this.props.Auth && this.props.Auth.accountTypeName) === CITY_HALL) {
              return null;
            }

            return (
              <ReportJob
                key={reportType}
                selectedCityHall={this.state.selectedCityHall}
                currentReportType={this.state.reportType}
                message={report.tooltip}
                label={report.label}
                reportLabel={this._getReportLabel(reportType)}
                reportType={reportType}
                className={this._isCheck(reportType)}
                SetValue={this._setValue}
                season={this.state.season}
              >
                {this.renderIcon(reportType)}
              </ReportJob>
            );
          }

          return (
            <ReportType
              key={reportType}
              message={report.tooltip}
              label={report.label}
              reportType={reportType}
              className={this._isCheck(reportType)}
              SetValue={this._setValue}
            >
              {this.renderIcon(reportType)}
            </ReportType>
          );
        })}
      </div>
    );
  };

  render() {
    const disabledPDF = !isAccountType(CITY_HALL, this.props.Auth) && this._excludedReports(this.state.reportType);
    const disabled = !isAccountType(CITY_HALL, this.props.Auth)
      ? !this.state.selectedCityHall || this._excludedReports(this.state.reportType)
      : false;

    return (
      <Container className="report-container px-5 py-4 my-4">
        <BackButton />

        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        <h4 className="mt-5 eon-title  eon-red force-wrap">{Localization.reportTitle}</h4>
        <Form>
          {!isAccountType(CITY_HALL, this.props.Auth) && (
            <SearchInput
              label={Localization.reportCityHallSelectLabel}
              places={this.state.cityHalls}
              onUpdate={this._getCityHalls}
              onChange={this._selectCityHall}
              disabled={this.state.loading}
              placeholder={Localization.reportCityHallPlaceholder}
              getLabel={(place?: Utils.ICityHall) => (place && place.name) || ''}
            />
          )}
          <fieldset className="mt-4 w-25 eon-select large">
            <Label>{Localization.reportSelectLabel}</Label>
            <br />
            <DatePicker
              name="startDate"
              id="startDate"
              value={this.state.startDate}
              onChange={this._setStartDate}
              className="eon-input-text"
              minDate={MIN_ADDITION_DATE}
            />
            <FormFeedback tooltip>{Localization.reportSelectError}</FormFeedback>
          </fieldset>

          {(this.props.Auth && this.props.Auth.accountTypeName) === ADMIN && (
            <div className="w-25 mt-4">
              <Label className="text-nowrap" for="stateBudgetAmount">
                {Localization.beneficiaryAddSeason}
              </Label>
              <Select
                value={Localization.beneficiaryAddSeasonSelect[this.state.season]}
                options={Localization.beneficiaryAddSeasonSelect}
                className="eon-react-select"
                styles={{
                  dropdownIndicator: (baseStyles: { [property: string]: string | number }) => ({
                    ...baseStyles,
                    backgroundColor: '#f11c0a',
                    color: 'white',
                    margin: '2px 2px 2px -1px',
                    ':hover': {
                      color: 'white',
                      backgroundColor: '#d51607'
                    }
                  }),
                  control: (baseStyles: { [property: string]: string | number }) => ({
                    ...baseStyles,
                    cursor: 'pointer',
                    border: '2px solid #bfbfbf'
                  }),
                  singleValue: (baseStyles: { [property: string]: string | number }) => ({
                    ...baseStyles,
                    color: '#666'
                  })
                }}
                onChange={(selectedOption: any) => {
                  this.setState({ season: selectedOption.value });
                }}
              />
            </div>
          )}

          <div className="my-5">
            <div className="d-flex">
              <StopSvg className="my-auto mx-3" />
              <p className="my-auto">{Localization.formatString(Localization.reportNaturalGasCount, this.state.gnCount)}</p>
            </div>
            <div className="d-flex">
              <StopSvg className="my-auto mx-3" />
              <p className="my-auto">{Localization.formatString(Localization.reportElectricEnergyCount, this.state.eeCount)}</p>
            </div>
            <div className="d-flex">
              <StopSvg className="my-auto mx-3" />
              <p className="my-auto">{Localization.formatString(Localization.reportAdditionNaturalGasCount, this.state.gnCountAddition)}</p>
            </div>
            <div className="d-flex">
              <StopSvg className="my-auto mx-3" />
              <p className="my-auto">
                {Localization.formatString(Localization.reportAdditionElectricEnergyCount, this.state.eeCountAddition)}
              </p>
            </div>
          </div>
          <hr />
          {this.renderReportsContainer()}
          <hr />
          <div className="d-flex mt-5">
            <Button
              type="button"
              name="xls"
              disabled={disabled}
              onClick={this._doSubmit}
              className="eon-button bg-eon-red eon-white ml-auto mr-5"
            >
              <span className="px-2">{Localization.reportSubmitFormatOption2}</span>
            </Button>
            <Button
              type="button"
              name="pdf"
              disabled={disabled || disabledPDF}
              onClick={this._doSubmit}
              className="eon-button bg-eon-red eon-white ml-5 mr-auto"
            >
              <span className="px-2">{Localization.reportSubmitFormatOption1}</span>
            </Button>
          </div>
        </Form>
      </Container>
    );
  }

  _isCheck = (type: Report.ReportType) => {
    return this.state.reportType === type ? 'checked bg-eon-white' : 'bg-eon-red';
  };

  _setStartDate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.persist();

    await this._setValue(event);

    return this._countAids();
  };

  _selectCityHall = async (selectedCityHall?: Utils.ICityHall) => {
    await this.setState({ selectedCityHall, cityHalls: undefined });

    if (selectedCityHall) return this._countAids();

    return;
  };

  _setValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const element = event.currentTarget || event.target || {};

    return this.setState(prevState => ({ ...prevState, [element.id || element.name]: element.value }));
  };

  _getCityHalls = async (search: string) => {
    try {
      if (!search || search.length < 3) {
        const canCount = !!this.state.selectedCityHall;

        await this.setState({ selectedCityHall: undefined, cityHalls: undefined });

        return canCount && this._countAids();
      }

      const response = await UtilApi.SearchCityHall(search);

      return this.setState({ cityHalls: response.data });
    } catch (error) {
      return Notifications.error(error && error.message);
    }
  };

  _countAids = async () => {
    try {
      await this.setState({ loading: true });

      const response = await AidsApi.CountAid({
        cityHallId: ToNumber(this.state.selectedCityHall && this.state.selectedCityHall.cityHallId) || undefined,
        startDate: this.state.startDate
      });

      const data = response.data || {};

      return this.setState({
        eeCount: data.eeCount || 0,
        gnCount: data.gnCount || 0,
        eeCountAddition: data.eeCountAddition || 0,
        gnCountAddition: data.gnCountAddition || 0,
        loading: false
      });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _doSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      event.preventDefault();

      const element = event.currentTarget || event.target || {};

      const reportFormat = element && element.name;

      await this.setState({ loading: true });

      const data = {
        cityHallId: ToNumber(this.state.selectedCityHall && this.state.selectedCityHall.cityHallId) || undefined,
        month: ToString(this.state.startDate),
        reportFormat: ToString(reportFormat),
        reportType: ToString(this.state.reportType)
      };

      const response = await AidsApi.ExportReport(data);

      const reportLabel = this._getReportLabel(data.reportType as Report.ReportType);

      if (reportLabel) {
        await DownloadFile(response.data, `${data.month}-${reportLabel}.${data.reportFormat}`);
      }

      return this.setState({ loading: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _getReportLabel = (reportType = this.state.reportType) => {
    const report = this._getReportFromReportType(reportType);

    return (report && report.label.replace(/(\r\n|\n|\r|)/gm, '').replace(/ +/gm, ' ')) || '';
  };

  _getReportFromReportType = (reportType = this.state.reportType) => {
    return Localization.reportTypeOptions.find(report => report.value === reportType);
  };
}

const mapStateToProps = (state: General.Store) => ({ Auth: state.Auth });

const mapDispatchToProps = (dispatch: Dispatch) => ({ ...bindActionCreators({}, dispatch) });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Report);
